// =====================================================
// LED 产品 5 级层级状态管理
// 参考 Apple 产品层级设计 + 乐观更新模式
// =====================================================

import { create } from 'zustand';
import { supabase } from '@/config/supabase';
import type {
  ProductCategory,
  ProductSeries,
  ProductFamily,
  ProductModelV2,
  ProductSKU,
  BreadcrumbItem,
  TreeNode,
  ViewMode,
  HierarchyPath,
  HierarchyFilter,
  CreateSeriesData,
  UpdateSeriesData,
  CreateFamilyData,
  UpdateFamilyData,
} from '../types/hierarchy';
import { CategoryLabels } from '../types/hierarchy';

// =====================================================
// Store 接口定义
// =====================================================

interface HierarchyState {
  // ===== 视图模式 =====
  viewMode: ViewMode;
  
  // ===== 当前路径 =====
  currentPath: HierarchyPath;
  breadcrumbs: BreadcrumbItem[];
  
  // ===== 选中状态 =====
  selectedCategory: ProductCategory | null;
  selectedSeries: ProductSeries | null;
  selectedFamily: ProductFamily | null;
  selectedModel: ProductModelV2 | null;
  selectedSKU: ProductSKU | null;
  
  // ===== 数据列表 =====
  seriesList: ProductSeries[];
  familiesList: ProductFamily[];
  modelsList: ProductModelV2[];
  skusList: ProductSKU[];
  
  // ===== 树形数据 =====
  treeData: TreeNode[];
  expandedNodes: Set<string>;
  
  // ===== 加载状态 =====
  isLoading: boolean;
  isLoadingTree: boolean;
  error: string | null;
  
  // ===== 视图模式 Actions =====
  setViewMode: (mode: ViewMode) => void;
  
  // ===== 导航 Actions =====
  navigateToCategory: (category: ProductCategory) => void;
  navigateToSeries: (series: ProductSeries) => void;
  navigateToFamily: (family: ProductFamily) => void;
  navigateToModel: (model: ProductModelV2) => void;
  navigateToSKU: (sku: ProductSKU) => void;
  navigateBack: () => void;
  navigateToLevel: (level: number, id?: string) => void;
  
  // ===== 数据获取 Actions =====
  fetchSeriesByCategory: (category: ProductCategory) => Promise<void>;
  fetchFamiliesBySeries: (seriesId: string) => Promise<void>;
  fetchModelsByFamily: (familyId: string) => Promise<void>;
  fetchSKUsByModel: (modelId: string) => Promise<void>;
  fetchTreeData: () => Promise<void>;
  
  // ===== Series CRUD =====
  createSeries: (data: CreateSeriesData) => Promise<ProductSeries>;
  updateSeries: (id: string, data: UpdateSeriesData) => Promise<void>;
  deleteSeries: (id: string) => Promise<void>;
  
  // ===== Family CRUD =====
  createFamily: (data: CreateFamilyData) => Promise<ProductFamily>;
  updateFamily: (id: string, data: UpdateFamilyData) => Promise<void>;
  deleteFamily: (id: string) => Promise<void>;
  
  // ===== 树形视图 Actions =====
  toggleTreeNode: (nodeId: string) => void;
  selectTreeNode: (node: TreeNode) => void;
  
  // ===== 工具方法 =====
  clearError: () => void;
  reset: () => void;
}

// =====================================================
// 初始状态
// =====================================================

const initialState = {
  viewMode: 'breadcrumb' as ViewMode,
  currentPath: {},
  breadcrumbs: [{ level: 1 as const, type: 'category' as const, name: '全部产品' }],
  selectedCategory: null,
  selectedSeries: null,
  selectedFamily: null,
  selectedModel: null,
  selectedSKU: null,
  seriesList: [],
  familiesList: [],
  modelsList: [],
  skusList: [],
  treeData: [],
  expandedNodes: new Set<string>(),
  isLoading: false,
  isLoadingTree: false,
  error: null,
};

// =====================================================
// Store 实现
// =====================================================

export const useHierarchyStore = create<HierarchyState>((set, get) => ({
  ...initialState,

  // ===== 视图模式 =====
  setViewMode: (mode) => {
    set({ viewMode: mode });
    // 切换到树形视图时，加载树形数据
    if (mode === 'tree' && get().treeData.length === 0) {
      get().fetchTreeData();
    }
  },

  // ===== 导航到分类 =====
  navigateToCategory: async (category) => {
    set({
      selectedCategory: category,
      selectedSeries: null,
      selectedFamily: null,
      selectedModel: null,
      selectedSKU: null,
      currentPath: { category },
      breadcrumbs: [
        { level: 1, type: 'category', name: '全部产品' },
        { level: 1, type: 'category', id: category, name: CategoryLabels[category] },
      ],
    });
    await get().fetchSeriesByCategory(category);
  },

  // ===== 导航到系列 =====
  navigateToSeries: async (series) => {
    const state = get();
    set({
      selectedSeries: series,
      selectedFamily: null,
      selectedModel: null,
      selectedSKU: null,
      currentPath: { ...state.currentPath, seriesId: series.id },
      breadcrumbs: [
        { level: 1, type: 'category', name: '全部产品' },
        { level: 1, type: 'category', id: series.category, name: CategoryLabels[series.category] },
        { level: 2, type: 'series', id: series.id, name: series.name },
      ],
    });
    await get().fetchFamiliesBySeries(series.id);
  },

  // ===== 导航到产品族 =====
  navigateToFamily: async (family) => {
    const state = get();
    set({
      selectedFamily: family,
      selectedModel: null,
      selectedSKU: null,
      currentPath: { ...state.currentPath, familyId: family.id },
      breadcrumbs: [
        ...state.breadcrumbs.slice(0, 3),
        { level: 3, type: 'family', id: family.id, name: family.name },
      ],
    });
    await get().fetchModelsByFamily(family.id);
  },

  // ===== 导航到型号 =====
  navigateToModel: async (model) => {
    const state = get();
    set({
      selectedModel: model,
      selectedSKU: null,
      currentPath: { ...state.currentPath, modelId: model.id },
      breadcrumbs: [
        ...state.breadcrumbs.slice(0, 4),
        { level: 4, type: 'model', id: model.id, name: model.name },
      ],
    });
    await get().fetchSKUsByModel(model.id);
  },

  // ===== 导航到 SKU =====
  navigateToSKU: (sku) => {
    const state = get();
    set({
      selectedSKU: sku,
      currentPath: { ...state.currentPath, skuId: sku.id },
      breadcrumbs: [
        ...state.breadcrumbs.slice(0, 5),
        { level: 5, type: 'sku', id: sku.id, name: sku.model_name },
      ],
    });
  },

  // ===== 返回上一级 =====
  navigateBack: () => {
    const state = get();
    const breadcrumbs = state.breadcrumbs;
    
    if (breadcrumbs.length <= 1) return;
    
    const newBreadcrumbs = breadcrumbs.slice(0, -1);
    const lastItem = newBreadcrumbs[newBreadcrumbs.length - 1];
    
    // 根据最后一项类型恢复状态
    if (lastItem.type === 'category' && lastItem.id) {
      get().navigateToCategory(lastItem.id as ProductCategory);
    } else if (lastItem.type === 'series' && lastItem.id) {
      const series = state.seriesList.find(s => s.id === lastItem.id);
      if (series) get().navigateToSeries(series);
    } else if (lastItem.type === 'family' && lastItem.id) {
      const family = state.familiesList.find(f => f.id === lastItem.id);
      if (family) get().navigateToFamily(family);
    } else if (lastItem.type === 'model' && lastItem.id) {
      const model = state.modelsList.find(m => m.id === lastItem.id);
      if (model) get().navigateToModel(model);
    } else {
      // 返回到根目录
      set({
        ...initialState,
        viewMode: state.viewMode,
        treeData: state.treeData,
        expandedNodes: state.expandedNodes,
      });
    }
  },

  // ===== 导航到指定层级 =====
  navigateToLevel: (level, id) => {
    const state = get();
    
    if (level === 1 && !id) {
      // 返回到根目录
      set({
        selectedCategory: null,
        selectedSeries: null,
        selectedFamily: null,
        selectedModel: null,
        selectedSKU: null,
        currentPath: {},
        breadcrumbs: [{ level: 1, type: 'category', name: '全部产品' }],
        familiesList: [],
        modelsList: [],
        skusList: [],
      });
    } else if (level === 1 && id) {
      get().navigateToCategory(id as ProductCategory);
    } else if (level === 2 && id) {
      const series = state.seriesList.find(s => s.id === id);
      if (series) get().navigateToSeries(series);
    } else if (level === 3 && id) {
      const family = state.familiesList.find(f => f.id === id);
      if (family) get().navigateToFamily(family);
    } else if (level === 4 && id) {
      const model = state.modelsList.find(m => m.id === id);
      if (model) get().navigateToModel(model);
    }
  },

  // ===== 获取系列列表 =====
  fetchSeriesByCategory: async (category) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('v_product_series_with_stats')
        .select('*')
        .eq('category', category)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ seriesList: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching series:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // ===== 获取产品族列表 =====
  fetchFamiliesBySeries: async (seriesId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('v_product_families_with_stats')
        .select('*')
        .eq('series_id', seriesId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ familiesList: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching families:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // ===== 获取型号列表 =====
  fetchModelsByFamily: async (familyId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('ditu_products')
        .select('*')
        .eq('family_id', familyId)
        .eq('is_active', true)
        .order('pitch', { ascending: true });

      if (error) throw error;
      set({ modelsList: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching models:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // ===== 获取 SKU 列表 =====
  fetchSKUsByModel: async (modelId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('ditu_product_models')
        .select('*')
        .eq('product_id', modelId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ skusList: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching SKUs:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // ===== 获取树形数据 =====
  fetchTreeData: async () => {
    set({ isLoadingTree: true, error: null });
    try {
      const { data, error } = await supabase.rpc('get_product_hierarchy_tree');
      
      if (error) throw error;
      
      // 转换为 TreeNode 格式
      const transformToTreeNodes = (items: any[], parentCategory?: ProductCategory): TreeNode[] => {
        return items.map(item => ({
          id: item.id,
          name: item.name,
          level: item.type === 'series' ? 2 : item.type === 'family' ? 3 : item.type === 'model' ? 4 : 5,
          type: item.type,
          children: item.children ? transformToTreeNodes(item.children, item.category || parentCategory) : undefined,
          isExpanded: false,
          isSelected: false,
          data: {
            category: item.category || parentCategory,
            pitch: item.pitch,
            count: item.children?.length,
          },
        })) as TreeNode[];
      };
      
      // 按分类分组
      const categories: ProductCategory[] = ['all_in_one', 'product', 'module'];
      const treeData: TreeNode[] = categories.map(category => ({
        id: category,
        name: CategoryLabels[category],
        level: 1,
        type: 'category',
        isExpanded: false,
        isSelected: false,
        children: transformToTreeNodes(
          (data || []).filter((s: any) => s.category === category),
          category
        ),
        data: { category },
      }));

      set({ treeData, isLoadingTree: false });
    } catch (error) {
      console.error('Error fetching tree data:', error);
      set({ error: (error as Error).message, isLoadingTree: false });
    }
  },

  // ===== 创建系列 (乐观更新) =====
  createSeries: async (data) => {
    const tempId = `temp-${Date.now()}`;
    const tempSeries: ProductSeries = {
      id: tempId,
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // 乐观更新
    set(state => ({
      seriesList: [...state.seriesList, tempSeries],
    }));

    try {
      const { data: newSeries, error } = await supabase
        .from('ditu_product_series')
        .insert(data)
        .select()
        .single();

      if (error) throw error;

      // 替换临时数据
      set(state => ({
        seriesList: state.seriesList.map(s => s.id === tempId ? newSeries : s),
      }));

      return newSeries;
    } catch (error) {
      // 回滚
      set(state => ({
        seriesList: state.seriesList.filter(s => s.id !== tempId),
        error: (error as Error).message,
      }));
      throw error;
    }
  },

  // ===== 更新系列 (乐观更新) =====
  updateSeries: async (id, data) => {
    const state = get();
    const oldSeries = state.seriesList.find(s => s.id === id);

    // 乐观更新
    set(state => ({
      seriesList: state.seriesList.map(s =>
        s.id === id ? { ...s, ...data, updated_at: new Date().toISOString() } : s
      ),
    }));

    try {
      const { error } = await supabase
        .from('ditu_product_series')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      // 回滚
      if (oldSeries) {
        set(state => ({
          seriesList: state.seriesList.map(s => s.id === id ? oldSeries : s),
          error: (error as Error).message,
        }));
      }
      throw error;
    }
  },

  // ===== 删除系列 (乐观更新) =====
  deleteSeries: async (id) => {
    const state = get();
    const deletedSeries = state.seriesList.find(s => s.id === id);
    const deletedIndex = state.seriesList.findIndex(s => s.id === id);

    // 乐观更新
    set(state => ({
      seriesList: state.seriesList.filter(s => s.id !== id),
    }));

    try {
      const { error } = await supabase
        .from('ditu_product_series')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      // 回滚
      if (deletedSeries) {
        set(state => {
          const seriesList = [...state.seriesList];
          seriesList.splice(deletedIndex, 0, deletedSeries);
          return { seriesList, error: (error as Error).message };
        });
      }
      throw error;
    }
  },

  // ===== 创建产品族 (乐观更新) =====
  createFamily: async (data) => {
    const tempId = `temp-${Date.now()}`;
    const tempFamily: ProductFamily = {
      id: tempId,
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // 乐观更新
    set(state => ({
      familiesList: [...state.familiesList, tempFamily],
    }));

    try {
      const { data: newFamily, error } = await supabase
        .from('ditu_product_families')
        .insert(data)
        .select()
        .single();

      if (error) throw error;

      // 替换临时数据
      set(state => ({
        familiesList: state.familiesList.map(f => f.id === tempId ? newFamily : f),
      }));

      return newFamily;
    } catch (error) {
      // 回滚
      set(state => ({
        familiesList: state.familiesList.filter(f => f.id !== tempId),
        error: (error as Error).message,
      }));
      throw error;
    }
  },

  // ===== 更新产品族 (乐观更新) =====
  updateFamily: async (id, data) => {
    const state = get();
    const oldFamily = state.familiesList.find(f => f.id === id);

    // 乐观更新
    set(state => ({
      familiesList: state.familiesList.map(f =>
        f.id === id ? { ...f, ...data, updated_at: new Date().toISOString() } : f
      ),
    }));

    try {
      const { error } = await supabase
        .from('ditu_product_families')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      // 回滚
      if (oldFamily) {
        set(state => ({
          familiesList: state.familiesList.map(f => f.id === id ? oldFamily : f),
          error: (error as Error).message,
        }));
      }
      throw error;
    }
  },

  // ===== 删除产品族 (乐观更新) =====
  deleteFamily: async (id) => {
    const state = get();
    const deletedFamily = state.familiesList.find(f => f.id === id);
    const deletedIndex = state.familiesList.findIndex(f => f.id === id);

    // 乐观更新
    set(state => ({
      familiesList: state.familiesList.filter(f => f.id !== id),
    }));

    try {
      const { error } = await supabase
        .from('ditu_product_families')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      // 回滚
      if (deletedFamily) {
        set(state => {
          const familiesList = [...state.familiesList];
          familiesList.splice(deletedIndex, 0, deletedFamily);
          return { familiesList, error: (error as Error).message };
        });
      }
      throw error;
    }
  },

  // ===== 展开/收起树节点 =====
  toggleTreeNode: (nodeId) => {
    set(state => {
      const newExpandedNodes = new Set(state.expandedNodes);
      if (newExpandedNodes.has(nodeId)) {
        newExpandedNodes.delete(nodeId);
      } else {
        newExpandedNodes.add(nodeId);
      }
      return { expandedNodes: newExpandedNodes };
    });
  },

  // ===== 选择树节点 =====
  selectTreeNode: (node) => {
    const state = get();
    
    // 根据节点类型导航
    if (node.type === 'category' && node.data?.category) {
      get().navigateToCategory(node.data.category);
    } else if (node.type === 'series') {
      const series = state.seriesList.find(s => s.id === node.id);
      if (series) get().navigateToSeries(series);
    } else if (node.type === 'family') {
      const family = state.familiesList.find(f => f.id === node.id);
      if (family) get().navigateToFamily(family);
    } else if (node.type === 'model') {
      const model = state.modelsList.find(m => m.id === node.id);
      if (model) get().navigateToModel(model);
    } else if (node.type === 'sku') {
      const sku = state.skusList.find(s => s.id === node.id);
      if (sku) get().navigateToSKU(sku);
    }
  },

  // ===== 清除错误 =====
  clearError: () => {
    set({ error: null });
  },

  // ===== 重置状态 =====
  reset: () => {
    set(initialState);
  },
}));

// =====================================================
// 导出类型
// =====================================================
export type { HierarchyState };
